// Context provider that runs `useWorkspacePresence` once at the AuthedLayout
// level and exposes the resulting map + setter to anywhere in the tree.
//
// Single hook → single WebSocket → consumed by:
//   - <TicketRow> (dashboard list dots)
//   - ticket-detail (calls setMyTicket on mount/unmount)
//
// Without this context the dashboard would either need its own
// presence-channel subscription (extra socket, extra cost) or pass props
// through every list-row level.

import { createContext, useContext, type ReactNode } from 'react';
import { useWorkspacePresence, type WorkspacePresenceEntry } from './use-workspace-presence';
import { useAuth } from './auth-guard';

interface ContextValue {
  presenceByTicket: Map<string, WorkspacePresenceEntry[]>;
  setMyTicket: (ticketId: string | null) => void;
}

const Ctx = createContext<ContextValue>({
  presenceByTicket: new Map(),
  setMyTicket: () => {},
});

export function WorkspacePresenceProvider({ children }: { children: ReactNode }) {
  const { teamMember } = useAuth();
  const me = teamMember
    ? {
        slack_user_id: teamMember.slack_user_id,
        name: teamMember.name,
        avatar_url: teamMember.avatar_url ?? null,
        email: teamMember.email,
      }
    : null;
  const value = useWorkspacePresence(me);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWorkspacePresenceContext(): ContextValue {
  return useContext(Ctx);
}

// Convenience: list of OTHER teammates on this specific ticket.
export function useTicketViewers(ticketId: string | null | undefined): WorkspacePresenceEntry[] {
  const { presenceByTicket } = useContext(Ctx);
  if (!ticketId) return [];
  return presenceByTicket.get(ticketId) ?? [];
}
